<template>
    <IndependentOffersPage
        v-if="isIndependentOffer"
        ref="independent"
        :offers="offers"
        :pageData="pageData"
        :applicationData="loanValues"
        :expired="expired"
        :loaded="loaded"
    />

    <DefaultLayout v-else :isAgentPage="isAgentPage" :isCrmPage="isCrmPage">
        <div v-if="isLoaded">
            <FilterModal v-if="!isAgentPage" :modalOpen="modalResubmitOpen">
                <template v-slot:heading>
                    <h2>{{ $t('results.resubmit.modal.heading') }}</h2>
                </template>

                <template #headerContent>
                    <span><i class="fal fa-times"></i></span>
                </template>

                <template v-slot:content>
                    <Resubmit :loanValues="loanValues" :hidden="!modalResubmitOpen" />
                </template>
            </FilterModal>

            <ResultHero
                v-if="!isAgentPage"
                :applicationData="loanValues"
                :declinedLenders="declinedLenders"
                :numberOfOffers="numberOfNonFilteredOffers"
                :isCreditCardCampaign="isCreditCardCampaign"
                :numberOfExpiredOffers="numberOfExpiredOffers"
                :isDebtSolutionOffer="isDebtSolutionOffer"
                :offers="offers"
                :hasNonAlternativeOffers="hasNonAlternativeOffers"
            />

            <div :style="[resultPageOverride]" class="results-body">
                <div class="container offer-container">
                    <div v-if="showMoneyAdviceWarning" class="result-warning">
                        <i class="fal fa-info-circle"></i> {{ $t('results.warningLatePayments') }}
                        <a href="https://www.moneyadviceservice.org.uk">moneyadviceservice.org.uk</a>
                    </div>

                    <Filters
                        v-if="
                            offerResultDataFilters && numberOfNonFilteredOffers && numberOfStandardOffers && !declined
                        "
                        ref="filters"
                        :isAgentPage="isAgentPage"
                        :offersHaveFees="offersHaveFees"
                        :offers="offers"
                    />

                    <AnnouncementBanner
                        :filtersPresent="
                            offerResultDataFilters && numberOfNonFilteredOffers && numberOfStandardOffers && !declined
                        "
                    />

                    <div v-if="numberOfOffers && hasNonAlternativeOffers">
                        <OfferListing
                            :applicationData="loanValues"
                            :declinedLenders="declinedLenders"
                            :expired="expired"
                            :isAgentPage="isAgentPage"
                            :offers="offers"
                            :ignoreOrdering="ignoreOrdering"
                            :fastFundingFlagStatus="fastFundingFlagStatus"
                            :alternativeOffers="alternativeOffers"
                        />
                    </div>

                    <div v-if="declinedLenderCount > 0 || isLendindtree" class="col">
                        <DeclinedLenders
                            :declinedLenders="declinedLenders"
                            :pageData="pageData.extras"
                            :hasNonAlternativeOffers="hasNonAlternativeOffers"
                            :status="status"
                        >
                            <template v-slot:offerListing v-if="alternativeOffers">
                                <OfferListing
                                    :applicationData="loanValues"
                                    :declinedLenders="declinedLenders"
                                    :expired="expired"
                                    :isAgentPage="isAgentPage"
                                    :offers="offers"
                                    :ignoreOrdering="ignoreOrdering"
                                    :fastFundingFlagStatus="fastFundingFlagStatus"
                                    ignoreCreditCardAlternativeOValidation="true"
                                    :alternativeOffers="alternativeOffers"
                                />
                            </template>
                        </DeclinedLenders>
                    </div>

                    <div v-else-if="status === 'declined'" class="col">
                        <h2 class="mt-5">
                            {{ declinedCopy?.declined_heading || $t('results.declinedLenders.declinedByAll') }}
                        </h2>

                        <div class="declined-description">
                            {{ declinedCopy?.declined_description || $t('results.declinedLenders.declinedDesc') }}
                        </div>

                        <DeclinedInfoBlock :copy="declinedCopy" />
                    </div>

                    <div v-if="!isLendindtree" class="result-disclaimer" v-html="disclaimer"></div>

                    <div :class="['text-center result-disclaimer-button', { 'd-none': isIframe }]">
                        <button class="button bordered to-top-button" @click="scrollToTop()">
                            {{ $t('results.toTop') }} <i class="far fa-arrow-up"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </DefaultLayout>

    <PreLoader :isAgentPage="isAgentPage" :show="(showPreloader || isIndependentLoaded) && !showErrorPage" />
</template>

<script>
import { url } from '@js/utilities/url.js'
import { updateOffers } from '@js/functions/resubmit.js'
import ApiRequest from '@js/utilities/apiRequest.js'
import { mapGetters, useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { fetchCampaignInfo } from '../utilities/campaignStatus'

// lodash components
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import some from 'lodash/some'
import get from 'lodash/get'
import { ref } from 'vue'

export default {
    props: {
        isAgentPage: {
            default: false,
        },
        isCrmPage: {
            default: false,
        },
    },
    setup() {
        const store = useStore()
        const router = useRouter()

        const independent = ref(null)

        return {
            router,
            store,
            independent,
        }
    },
    data() {
        return {
            showPreloader: true,
            campaignTypeResult: window.CAMPAIGN_TYPE,
            campaignIntegration: window.CAMPAIGN_INTEGRATION,
            loaded: false,
            eventTracked: false,
            minimumPreloaderTime: this.themeSetting('preloader_minimum_time') || 5,
            resultPageOverride: {},
            isIframe: false,
            timerStarted: false,
            timedOut: false,
            ignoreOrdering: false,
            trackedOffers: [],
            modalResubmitOpen: false,
            typeCreditCardOnlyVisibleOnly: true,
            showSortBlocks: false,
        }
    },
    async created() {
        if (!(await this.refreshAppData(this.$route.params.reference))) {
            return
        }

        this.addResultReference()
        this.setLoanData()

        await this.poll(this.$store.state.result?.allAppData?.application_data?.reference)

        window.APPLICATION_ID = this.loanValues?.reference || null

        this.trackEvent('pageview', { route: this.$route.fullPath })

        this.loaded = true
    },
    async mounted() {
        if (window.IS_IFRAME && window.innerWidth < 995) {
            this.resultPageOverride.position = 'initial'
        }

        this.isIframe = window.IS_IFRAME

        this.emitter.on('resubmitModal', this.reSubmit)
        this.emitter.on('sortSelected', this.trackOffersDisplayed)
        this.emitter.on('closeHeroModal', this.reSubmitClose)
        this.emitter.on('reSubmitted', this.reSubmitted)
        this.emitter.on('previousHistory', this.previousResultRequested)
        this.emitter.on('refreshOffer', async e => {
            this.showPreloader = true

            await this.setCampaignInfo()

            if (this.isCreditCardAndStatusExpired) {
                const appData = { ...this }
                const LOAN_PURPOSE_NUMBER = this.territory === 'gbr' ? 29 : 26

                appData.resubmitExpiredCreditCard = true
                appData.loanPurpose = LOAN_PURPOSE_NUMBER
                appData.emitter = this.emitter

                updateOffers(this.$store, appData)
            } else {
                updateOffers(this.$store, this)
            }

            this.trackOffersDisplayed()
        })
    },
    updated() {
        if (this.eventTracked || this.modalResubmitOpen) {
            return
        }

        let event

        if (this.isAgentPage) {
            event = 'agent_results_loaded'
        } else if (this.isCrmPage) {
            event = 'crm_results_loaded'
        } else {
            event = 'results_loaded'
        }

        this.trackEvent(event, { territory: process.env.TERRITORY, ...this.extractEventData() })
        this.eventTracked = true
    },
    methods: {
        async refreshAppData(ref) {
            return this.$store.dispatch('refreshAppData', { store: this.$store, ref: ref })
        },
        trackOffersDisplayed() {
            const offersArray = this.trackedOffers.map((offer, index) => ({
                offerId: offer.offerId,
                sortIndex: index + 1,
                territory: offer.territory,
            }))

            this.sendOffersInBatches(offersArray)
        },
        sendOffersInBatches(offersArray) {
            const maxChars = 100
            let currentBatch = []

            offersArray.forEach(offer => {
                const offerString = JSON.stringify(offer)

                if (JSON.stringify(currentBatch).length + offerString.length > maxChars) {
                    this.trackEvent('offer_displayed', {
                        offers: JSON.stringify(currentBatch),
                        ...this.extractEventData(),
                    })

                    currentBatch = []
                }

                currentBatch.push(offer)
            })

            if (currentBatch.length > 0) {
                this.trackEvent('offer_displayed', {
                    offers: JSON.stringify(currentBatch),
                    ...this.extractEventData(),
                })
            }
        },
        previousResultRequested: async function (previousResultReference) {
            await this.router.push({
                name: 'result',
                params: {
                    reference: previousResultReference,
                },
            })

            if (!(await this.refreshAppData(previousResultReference))) {
                return
            }

            this.setLoanData()

            await this.poll(this.$store.state.result.allAppData.application_data.reference)
        },
        extractEventData: function (vParams = false) {
            const eventData = {
                campaign_code: window.CAMPAIGN_CODE,
                campaign_integration: window.CAMPAIGN_INTEGRATION,
                application_id: this.$store.state.result.allAppData.application_data?.reference,
                territory: process.env.TERRITORY,
            }

            if (vParams.length > 0) {
                for (let i = 0; i < vParams.length; i++) {
                    eventData['v' + (i + 1)] = vParams[i]
                }
            }

            return eventData
        },
        trackResubmittedResultsEvent: function () {
            const vParams = this.extractVParams(this.$route.fullPath)
            const eventData = this.extractEventData(vParams)

            this.trackEvent('results_resubmitted', {
                ...eventData,
                ...this.extractEventData(),
                ...{ elementType: 'button', elementName: 'update search' },
            })
        },
        trackLoadedResubmitResultsEvent: function () {
            const vParams = this.extractVParams(this.$route.fullPath)
            const eventData = this.extractEventData(vParams)
            eventData.sortCategory = this.trackedOffers?.sortCategory

            this.trackEvent('resubmit_results_loaded', eventData)
        },
        reSubmitted: async function (result) {
            this.trackResubmittedResultsEvent()

            const url = result.declined_url ?? result.redirect_url
            const regex = /result\/([a-f0-9]{32})$/
            const matches = regex.exec(url)
            this.timedOut = false
            this.timerStarted = false

            if (matches !== null) {
                const ref = matches[1]
                this.addResultReference(ref)

                await this.router.push({ name: 'result', params: { reference: ref } })

                if (!(await this.refreshAppData(ref))) {
                    return
                }

                window.APPLICATION_ID = this.$store.state.result.allAppData?.application_data?.reference || ref
                this.setLoanData()

                await this.poll(this.$store.state.result.allAppData.application_data.reference)

                this.trackLoadedResubmitResultsEvent()
                this.reSubmitClose()
            }
        },
        addResultReference(reference = null) {
            if (!reference) {
                if (this.$store.state.result.allAppData && this.$store.state.result.allAppData.reference) {
                    reference = this.$store.state.result.allAppData.reference
                }
            }

            this.store.commit('addResultReference', reference)
        },
        setLoanData() {
            this.loanAmount = this?.loanValues?.loan_amount
            this.loanPurpose = this?.loanValues?.loan_purpose

            if (this.excludeTerritories('usa')) {
                this.loanTerm = parseInt(this?.loanValues?.loan_term)
            }

            this.store.commit('resetPageStatus')
        },
        scrollToTop: function () {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            })
        },
        poll: async function (reference) {
            const pollingEndpoint = url(`/monevo/poll_results/${reference}`)
            const pollInterval = 5000

            const startPollingTime = performance.now()

            this.showPreloader = true

            if (!this.timerStarted) {
                this.timerStarted = true
                let resultPageTimeOut = 40000

                if (this.includeTerritories('aus')) {
                    resultPageTimeOut = 60000
                }

                setTimeout(() => {
                    this.timedOut = true
                }, resultPageTimeOut)
            }

            const responseData = await ApiRequest.get(this.$store, pollingEndpoint)

            // Waiting for response, call results again
            if (responseData.awaiting_response && responseData.awaiting_response !== 0 && this.timedOut === false) {
                setTimeout(() => {
                    this.poll(reference)
                }, pollInterval)

                return false
            }

            // only set this data after polling complete to avoid setting off events everytime the data gets replaced
            this.store.commit('setOfferResultData', responseData)

            if (responseData.ignore_ordering) {
                this.ignoreOrdering = responseData.ignore_ordering
            }

            this.trackOfferWall(responseData.offer_groups)

            if (responseData.declined_url) {
                this.store.commit('setPageStatus', 'declined')
                this.showPreloader = false
                return false
            }

            if (!this.isAgentPage && responseData.redirect_url && this.acceptRedirect(responseData.accepted_redirect)) {
                window.location.replace(responseData.redirect_url)
                return false
            }

            if (this.showMoneyAdviceWarning) {
                const totalRepaymentSort = find(this.store.getters.sortOptions, function (sortOption) {
                    return sortOption.field === 'total_repayment'
                })

                if (totalRepaymentSort) {
                    const currentSort = this.getCurrentSortSelected
                    currentSort.active = false
                    totalRepaymentSort.active = true
                }
            }

            if (responseData.results_expired) {
                this.store.commit('setPageStatus', 'expired')
                this.showPreloader = false
                return false
            }

            const apiCallTime = performance.now() - startPollingTime
            const minimumTimeLeft = this.$route.params.isDirectRoute
                ? 0
                : this.minimumPreloaderTime * 1000 - apiCallTime
            if (minimumTimeLeft > 0) {
                setTimeout(() => {
                    this.showPreloader = false
                }, minimumTimeLeft)
            } else {
                this.showPreloader = false
            }

            return true
        },
        acceptRedirect(acceptedRedirect) {
            if (acceptedRedirect) {
                return acceptedRedirect === 'true' && this.isAgentPage === false && this.isCrmPage === false
            }
            return true
        },
        trackOfferWall: function (offerGroups) {
            let count = 0
            const offerTypeCount = []

            forEach(offerGroups, offerGroup => {
                forEach(offerGroup.offers, offer => {
                    count++
                    offerTypeCount[offer.type] = offer.type in offerTypeCount ? offerTypeCount[offer.type] + 1 : 1
                })
            })

            for (const key in offerTypeCount) {
                this.trackEvent('event', {
                    eventCategory: 'offerwall',
                    eventAction: key,
                    eventLabel: offerTypeCount[key],
                    eventValue: offerTypeCount[key],
                    ...this.extractEventData(),
                })
            }
            //track number of offers
            this.trackEvent('event', {
                eventCategory: 'offerwall',
                eventAction: 'no_of_offers',
                eventLabel: count,
                eventValue: count,
                ...this.extractEventData(),
            })
        },
        async setCampaignInfo() {
            const campaignInfo = await fetchCampaignInfo(this.pageData.campaign_code)

            this.campaignTypeResult = campaignInfo?.campaign_type
            this.campaignIntegration = campaignInfo?.campaign_integration
        },
        isAlternativeOffer(offer) {
            if (!this.isCreditCardCampaign) {
                return (
                    offer.type === 'alternative' ||
                    (this.territory === 'usa' && (offer.type === 'credit card only' || offer.type === 'debt_solution'))
                )
            }

            return offer.type === 'alternative' || (this.territory === 'usa' && offer.type === 'debt_solution')
        },
        reSubmit: function () {
            this.modalResubmitOpen = true
        },
        reSubmitClose: function () {
            this.modalResubmitOpen = false
        },
    },
    computed: {
        ...mapGetters([
            'sortOptions',
            'getCurrentSortSelected',
            'offerResultDataFilters',
            'numberOfOffers',
            'numberOfNonFilteredOffers',
            'numberOfStandardOffers',
            'numberOfExpiredOffers',
            'declinedLenders',
            'defaultOrderOffers',
            'offersSorted',
            'offers',
            'loanValues',
            'declined',
            'expired',
        ]),
        isCreditCardAndStatusExpired: function () {
            return this.isCreditCardCampaign && this.status === 'expired'
        },
        declinedLenderCount: function () {
            return this.declinedLenders?.length
        },
        showErrorPage: function () {
            return this.store.state.global.data.showErrorPage
        },
        isLoaded() {
            return this.loaded && this.$store.state.result.offerResultData
        },
        fastFundingFlagStatus: function () {
            return this.$store.state.result.offerResultData.fastFundingFlagStatus
        },
        disclaimer: function () {
            return (
                (this.$store.state.result.offerResultData.content &&
                    this.$store.state.result.offerResultData.content.footer) ||
                this.$i18n.messages[this.$i18n.locale].results.terms ||
                null
            )
        },
        pageData: function () {
            return this.store.state.global.page || null
        },
        loanAmount: {
            get() {
                return this.store.getters.fieldValue('loanAmount', 'data', true)
            },
            set(value) {
                this.store.commit('setDataFieldValue', {
                    name: 'loanAmount',
                    value,
                })
            },
        },
        loanTerm: {
            get() {
                return this.store.getters.fieldValue('loanTerm', 'data', true)
            },
            set(value) {
                this.store.commit('setDataFieldValue', {
                    name: 'loanTerm',
                    value,
                })
            },
        },
        loanPurpose: {
            get() {
                return this.store.getters.fieldValue('loanPurpose', 'data', true)
            },
            set(value) {
                this.store.commit('setDataFieldValue', {
                    name: 'loanPurpose',
                    value,
                })
            },
        },
        isCreditCardCampaign() {
            return this.campaignTypeResult === 'Credit Card'
        },
        declinedCopy: function () {
            return (this.pageData && this.pageData.extras) || null
        },
        territory: function () {
            return window.TERRITORY
        },
        showMoneyAdviceWarning: function () {
            return (
                this.$store.state.result.offerResultData &&
                this.$store.state.result.offerResultData.show_money_advice_warning &&
                process.env.TERRITORY === 'gbr'
            )
        },
        status() {
            return this.$store.state.result.status
        },
        offersHaveFees() {
            forEach(this.offers, offer => {
                if (offer.fees > 0) {
                    return true
                }
            })

            return false
        },
        isIndependentOffer() {
            return window.IS_INDEPENDENT_RESULT === '1'
        },
        isDebtSolutionOffer() {
            return some(this.offers, ['type', 'debt_solution']) && this.territory === 'gbr'
        },
        isLendindtree: function () {
            return process.env.IS_LENDINDTREE
        },
        hasNonAlternativeOffers: function () {
            if (!this.isLendindtree || !this.numberOfOffers) {
                return true
            }

            return some(this.offers, offer => !this.isAlternativeOffer(offer))
        },
        alternativeOffers: function () {
            if (!this.isLendindtree || this.hasNonAlternativeOffers) {
                return false
            }

            return this.offers
        },
        canShowABTestSortBlocks: function () {
            if (this.offers.length < 4) {
                return false
            }

            return this.$store.state.result.offerResultData.showSortBlocks
        },
        isIndependentLoaded: function () {
            return this.isIndependentOffer && !this.independent?.isIndependentLoaded
        },
    },
    watch: {
        offers(newOffers) {
            this.trackedOffers = []
            // must set state here instead of store getter as they all rely on each other and would create infinite loops
            const order = {
                field: 'name',
                asc: true,
            }
            const orderedList = newOffers.sort((a, b) => {
                if (order.asc) {
                    if (a[order.field] > b[order.field]) {
                        return 1
                    } else if (a[order.field] < b[order.field]) {
                        return -1
                    } else {
                        return 0
                    }
                }
            })

            forEach(newOffers, (offer, index) => {
                if (
                    !this.isCreditCardCampaign ||
                    (this.isCreditCardCampaign && !this.isAlternativeOffer(offer)) ||
                    (this.isLendindtree && !this.hasNonAlternativeOffers)
                ) {
                    this.trackedOffers.push({
                        offerId: get(offer, 'id', null),
                        territory: process.env.TERRITORY,
                        sortIndex: index + 1,
                    })
                }

                if (offer.type !== 'credit card only') {
                    this.typeCreditCardOnlyVisibleOnly = false
                }
            })

            const activeSort = find(this.store.getters.sortOptions, function (sortOption) {
                return sortOption.active === true
            })

            this.trackEvent('event', {
                sortCategory: activeSort?.name,
                eventCategory: 'filtering',
                eventAction: 'load',
                eventLabel: 'sort_category',
            })
            this.trackOffersDisplayed()
            this.showSortBlocks = this.canShowABTestSortBlocks
        },
        typeCreditCardOnlyVisibleOnly() {
            this.$store.commit(
                'setActiveSortOptionList',
                this.typeCreditCardOnlyVisibleOnly ? 'creditCardOnly' : 'default',
            )
        },
        showSortBlocks() {
            this.$store.commit('setActiveSortOptionList', 'abTesting')
        },
    },
    provide() {
        return {
            application_id: this.loanValues?.reference,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/variables';
@import '~scss/settings/responsive';
@import '~scss/mixins/rem';

.results-body {
    position: relative;
    padding: 1px; // prevent collapsing margins

    .result-disclaimer {
        color: #858585;
        font-size: rem(14px);
        margin-top: rem(70px);

        @media screen and (max-width: $bp-small) {
            margin: rem(12.6px);
            text-align: justify;
        }
    }

    .result-disclaimer-button {
        margin-top: 3rem;
        margin-bottom: 3rem;

        @media screen and (max-width: $bp-small) {
            margin-top: 1.5rem;
        }
    }

    @media screen and (max-width: $bp-large) {
        background: $results-background;
    }

    @media screen and (max-width: $bp-small) {
        .offer-container {
            padding: 0;
        }
    }

    .result-warning {
        position: relative;
        font-size: rem(15px);
        padding: 30px 20px 30px 35px;
        border-bottom: 1px solid $color-grey;

        @media screen and (max-width: $bp-large) {
            font-size: rem(13px);
            background: $color-white;
            border: 1px solid $color-grey;
            padding: 12px 16px 12px 55px;
            margin-top: 16px;
            border-radius: 4px;
        }

        .fa-info-circle {
            font-size: 23px;
            top: 0;
            bottom: 0;
            left: 0;
            position: absolute;
            margin: auto 0;

            @media screen and (max-width: $bp-large) {
                left: 20px;
            }
        }
    }

    .to-top-button {
        background: transparent;
        color: var(--to-top-button);
        border-color: var(--to-top-button);
        padding-right: 60px;
        padding-left: 60px;
        width: auto;

        .fa-arrow-up {
            color: var(--to-top-button);
            margin-left: 10px;
        }
    }
}
</style>
